import { Stack } from '@mui/material';
import {
  audienceAnalysisConfig,
  audienceAnalysisDownloadReportColumns,
} from '../../data/audienceAnalysisData';
import { formatNumberUsingUnit } from '../../utils/formatUtils';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export const HOUSEHOLD_COMPONENT = 'household_data';
export const AUDIENCE_ANALYSIS_COMPONENT = 'audience_analysis_data';

export const comparisonFormatter = (value, keyValue, showComparison) => {
  const percentValue = (keyValue?.currentValue / keyValue?.compareValue) * 100;
  switch (true) {
    case percentValue === 0:
      return (
        <Stack direction={'row'} spacing={1}>
          <span>{value}</span>
        </Stack>
      );
    case percentValue === Infinity:
      return (
        <Stack direction={'row'} spacing={1}>
          <span>{value}</span>
        </Stack>
      );
    case percentValue > 100:
      return (
        <Stack direction={'row'} spacing={1}>
          <span>{value}</span>
          {showComparison ? (
            <div style={{ display: 'flex', alignItems: 'center', color: 'green' }}>
              {' '}
              (<ArrowUpwardIcon style={{ color: 'green', width: 15, height: 20 }} />
              <span style={{ color: 'green', fontSize: 10 }}>{percentValue.toFixed(2)}%</span>)
            </div>
          ) : (
            <></>
          )}
        </Stack>
      );

    case percentValue < 100:
      return (
        <Stack direction={'row'} spacing={1}>
          <span>{value}</span>
          {showComparison ? (
            <div style={{ display: 'flex', alignItems: 'center', color: 'red' }}>
              {' '}
              (<ArrowDownwardIcon style={{ color: 'red', width: 15, height: 20 }} />
              <span style={{ color: 'red', fontSize: 10 }}>{percentValue?.toFixed(2)}%</span>){' '}
            </div>
          ) : (
            <></>
          )}
        </Stack>
      );

    default:
      return (
        <Stack direction={'row'} spacing={1}>
          <span>{value}</span>
        </Stack>
      );
  }
};

export const comparePercentFormatter = (comparePercentValue) => {
  return (
    <Stack direction={'row'} spacing={1}>
      {comparePercentValue > 100 ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ArrowUpwardIcon style={{ color: 'green', width: 15, height: 20 }} />
          <span style={{ color: 'green' }}>{comparePercentValue.toFixed(2)}%</span>
        </div>
      ) : comparePercentValue < 100 ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ArrowDownwardIcon style={{ color: 'red', width: 15, height: 20 }} />
          <span style={{ color: 'red' }}>{comparePercentValue.toFixed(2)}%</span>
        </div>
      ) : comparePercentValue === 0 ? (
        `${comparePercentValue.toFixed(2)}%`
      ) : (
        'N/A'
      )}
    </Stack>
  );
};

export const Tiers = [
  {
    label: 'Tier A',
    value: 'tierA',
  },
  {
    label: 'Tier B',
    value: 'tierB',
  },
  {
    label: 'Tier C',
    value: 'tierC',
  },
  {
    label: 'Tier D',
    value: 'tierD',
  },
];

export const tiersData = (showIndicator) => [
  {
    Header: 'Tier A',
    accessor: 'tierA',
    Cell: ({ row }) => {
      return (
        <div>
          {row?.original?.tierA
            ? comparisonFormatter(
                row?.original?.tierA?.tierA,
                row?.original?.tierA?.keyValue,
                showIndicator
              )
            : 'N/A'}
        </div>
      );
    },
  },
  {
    Header: 'Tier B',
    accessor: 'tierB',
    Cell: ({ row }) => {
      return (
        <div>
          {row?.original?.tierB || row?.original?.tierB?.tierB === 0
            ? comparisonFormatter(
                row?.original?.tierB?.tierB,
                row?.original?.tierB?.keyValue,
                showIndicator
              )
            : 'N/A'}
        </div>
      );
    },
  },
  {
    Header: 'Tier C',
    accessor: 'tierC',
    Cell: ({ row }) => {
      return (
        <div>
          {row?.original?.tierC || row?.original?.tierC?.tierC === 0
            ? comparisonFormatter(
                row?.original?.tierC?.tierC,
                row?.original?.tierC?.keyValue,
                showIndicator
              )
            : 'N/A'}
        </div>
      );
    },
  },
  {
    Header: 'Tier D',
    accessor: 'tierD',
    Cell: ({ row }) => {
      return (
        <div>
          {row?.original?.tierD || row?.original?.tierD?.tierD === 0
            ? comparisonFormatter(
                row?.original?.tierD?.tierD,
                row?.original?.tierD?.keyValue,
                showIndicator
              )
            : 'N/A'}
        </div>
      );
    },
  },
];

const configureCellValues = (key, keyValue, unit, downloadData) => {
  let value = keyValue;
  let cell = {};
  if (key?.endsWith('_total'))
    cell = downloadData
      ? { total: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          total: value
            ? { total: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };

  if (key?.endsWith('_tier_a'))
    cell = downloadData
      ? { tierA: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          tierA: value
            ? { tierA: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };

  if (key?.endsWith('_tier_b'))
    cell = downloadData
      ? { tierB: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          tierB: value
            ? { tierB: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };

  if (key?.endsWith('_tier_c'))
    cell = downloadData
      ? { tierC: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          tierC: value
            ? { tierC: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };

  if (key?.endsWith('_tier_d'))
    cell = downloadData
      ? { tierD: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          tierD: value
            ? { tierD: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };

  if (key?.endsWith('_non_add'))
    cell = downloadData
      ? { nonAddressable: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          nonAddressable: value
            ? {
                nonAddressable: formatNumberUsingUnit(value?.currentValue, unit),
                keyValue: keyValue,
              }
            : 'N/A',
        };

  if (key?.endsWith('_nurture'))
    cell = downloadData
      ? { nurture: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          nurture: value
            ? { nurture: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };
  return cell;
};

const configureCellValuesHouseholdAvailable = (key, keyValue, unit, downloadData) => {
  let value = keyValue;
  let cell = {};
  if (key.startsWith('household_available'))
    cell = downloadData
      ? { total: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          total: value
            ? { total: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };

  if (key.startsWith('tier_a'))
    cell = downloadData
      ? { tierA: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          tierA: value
            ? { tierA: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };

  if (key.startsWith('tier_b'))
    cell = downloadData
      ? { tierB: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          tierB: value
            ? { tierB: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };

  if (key.startsWith('tier_c'))
    cell = downloadData
      ? { tierC: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          tierC: value
            ? { tierC: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };

  if (key.startsWith('tier_d'))
    cell = downloadData
      ? { tierD: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          tierD: value
            ? { tierD: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };

  if (key.startsWith('non_add'))
    cell = downloadData
      ? { nonAddressable: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          nonAddressable: value
            ? {
                nonAddressable: formatNumberUsingUnit(value?.currentValue, unit),
                keyValue: keyValue,
              }
            : 'N/A',
        };

  if (key.startsWith('nurture'))
    cell = downloadData
      ? { nurture: value ? formatNumberUsingUnit(value, unit) : 'N/A' }
      : {
          nurture: value
            ? { nurture: formatNumberUsingUnit(value?.currentValue, unit), keyValue: keyValue }
            : 'N/A',
        };
  return cell;
};

const configureRowValues = (dataArray, item, data, dataCompare) => {
  let obj = {};
  for (const key in dataArray) {
    let keyValue =
      data?.[key] && dataCompare?.[key]
        ? data[key]?.toString().includes('.')
          ? { currentValue: data[key]?.toFixed(2), compareValue: dataCompare[key]?.toFixed(2) }
          : { currentValue: data[key], compareValue: dataCompare[key] }
        : { currentValue: null, compareValue: null };
    let unit = item.unit === 'none' ? '' : item.unit;
    let cell =
      item.value === 'household_available'
        ? configureCellValuesHouseholdAvailable(key, keyValue, unit)
        : configureCellValues(key, keyValue, unit);
    obj = { ...obj, ...cell };
  }
  return obj;
};
const getCompareData = (item, audienceAnalysisCompare, audienceAnalysisCurrent) => {
  let obj = {};
  let totalCompare;
  let totalCurrent;
  for (const key in audienceAnalysisCompare) {
    if (key.endsWith('_total')) {
      obj = { ...obj, [key]: audienceAnalysisCompare[key] };
    }
  }
  for (const key in obj) {
    if (key.startsWith(item.value)) {
      totalCompare = obj[key];
      totalCurrent = audienceAnalysisCurrent[key];
    }
  }
  return { totalCurrent, totalCompare };
};

const configureSubRows = (item, data, dataCompare) => {
  let subRows = item?.subRows;
  let subRowsValues = subRows.map((row) => {
    let obj = {};
    for (const key in data) {
      if (key.startsWith(row.value)) obj = { ...obj, [key]: data[key] };
    }
    return obj;
  });

  let subRowsArray = subRowsValues.map((row, index) => {
    let obj = {};
    let comparePercent;
    for (const key in row) {
      let keyValue =
        data && data[key] !== undefined && dataCompare && dataCompare[key] !== undefined
          ? data[key]?.toString()?.includes('.')
            ? { currentValue: data[key]?.toFixed(2), compareValue: dataCompare[key]?.toFixed(2) }
            : { currentValue: data[key], compareValue: dataCompare[key] }
          : { currentValue: null, compareValue: null };
      let unit = item.unit === 'none' ? '' : item.unit;
      let cell = configureCellValues(key, keyValue, unit);
      let compareTotal = getCompareData(subRows[index], dataCompare, data);
      if (compareTotal?.totalCurrent && compareTotal?.totalCompare) {
        comparePercent = (compareTotal?.totalCurrent / compareTotal?.totalCompare) * 100;
      }
      obj = {
        ...obj,
        propertyName: 'Measured Results',
        channels: subRows[index].label,
        ...cell,
        comparePercent: comparePercent?.toFixed(2).toString()?.concat('%'),
        isSubrow: true,
      };
    }
    return obj;
  });
  return subRowsArray;
};

const configureRow = (item, data, dataCompare) => {
  let dataArray = {};
  let obj = {};
  let comparePercent;
  if (item.value === 'household_available') {
    for (const key in data) {
      if (key.endsWith('household_available'))
        dataArray = {
          ...dataArray,
          [key]: {
            currentValue: data[key],
            compareValue: dataCompare?.[key] ?? null,
          },
        };
    }
    if (data?.household_available && dataCompare?.household_available) {
      comparePercent = ((data?.household_available / dataCompare?.household_available) * 100)
        ?.toFixed(2)
        ?.toString()
        ?.concat('%');
    } else {
      comparePercent = 'N/A';
    }
    obj = { ...configureRowValues(dataArray, item, data, dataCompare), comparePercent };
  } else {
    if (item?.subRows) {
      for (const key in data) {
        if (key.startsWith(item.value))
          dataArray = {
            ...dataArray,
            [key]: { currentValue: data?.[key] ?? null, compareValue: dataCompare?.[key] ?? null },
          };
      }
      let subRowsArray = configureSubRows(item, data, dataCompare);
      let compareTotal = getCompareData(item, dataCompare, data);
      if (compareTotal?.totalCurrent && compareTotal?.totalCompare) {
        comparePercent = ((compareTotal?.totalCurrent / compareTotal?.totalCompare) * 100)
          ?.toFixed(2)
          ?.toString()
          ?.concat('%');
      } else {
        comparePercent = 'N/A';
      }
      obj = {
        ...configureRowValues(dataArray, item, data, dataCompare),
        expand: true,
        subRows: subRowsArray,
        comparePercent,
      };
    } else {
      for (const key in data) {
        if (key.startsWith(item.value))
          dataArray = {
            ...dataArray,
            [key]: { currentValue: data?.[key], compareValue: dataCompare?.[key] ?? null },
          };
      }
      let compareTotal = getCompareData(item, dataCompare, data);
      if (compareTotal?.totalCurrent && compareTotal?.totalCompare) {
        comparePercent = ((compareTotal?.totalCurrent / compareTotal?.totalCompare) * 100)
          ?.toFixed(2)
          ?.toString()
          ?.concat('%');
      } else {
        comparePercent = 'N/A';
      }
      obj = { ...configureRowValues(dataArray, item, data, dataCompare), comparePercent };
    }
  }
  return obj;
};

export const configureTableData = (
  isDownload,
  options,
  storeId,
  audienceAnalysisCurrent,
  householdAvailableCurrent,
  audienceAnalysisCompare,
  householdAvailableCompare
) => {
  const result = (isDownload ? options?.map((store) => store?.value) : [storeId])?.map((store) => {
    const currentStoreHousehold = householdAvailableCurrent?.find(
      (data) => data?.store_id === store
    );
    const compareStoreHousehold = householdAvailableCompare?.find(
      (data) => data?.store_id === store
    );
    const currentAudienceAnalysis = audienceAnalysisCurrent?.find(
      (data) => data?.store_id === store
    );
    const compareAudienceAnalysis = audienceAnalysisCompare?.find(
      (data) => data.store_id === store
    );

    return {
      store_id: store,
      rows: [
        ...audienceAnalysisConfig.measuredResult.map((item) =>
          item.value === 'household_available'
            ? {
                propertyName: 'Measured Results',
                channels: item.label,
                compareData: compareStoreHousehold?.household_available,
                ...configureRow(item, currentStoreHousehold, compareStoreHousehold),
              }
            : {
                propertyName: 'Measured Results',
                channels: item.label,
                ...configureRow(item, currentAudienceAnalysis, compareAudienceAnalysis),
              }
        ),
        ...audienceAnalysisConfig.calculatedPerformanceIndicators.map((item) => ({
          propertyName: 'Calculated Performance Indicators',
          channels: item.label,
          ...configureRow(item, currentAudienceAnalysis, compareAudienceAnalysis),
        })),
      ],
    };
  });
  const updateResult = removeEmptyObjects(result);
  return [{ store_id: result[0]?.store_id, rows: updateResult }];
};

// download report configurations and functions-> the calulation is different from the table data hence separated

const configureDownloadRowValues = (dataArray, item, data, downloadData) => {
  let obj = {};
  for (const key in dataArray) {
    let keyValue = data[key]?.toString().includes('.') ? data[key]?.toFixed(2) : data[key];
    let unit = item.unit === 'none' ? '' : item.unit;
    let cell =
      item.value === 'household_available'
        ? configureCellValuesHouseholdAvailable(key, keyValue, unit, downloadData)
        : configureCellValues(key, keyValue, unit, downloadData);
    obj = { ...obj, ...cell };
  }
  return obj;
};

const configureDownloadSubRows = (item, data, dataCompare, downloadData) => {
  let subRows = item?.subRows;
  let subRowsValues = subRows.map((row) => {
    let obj = {};
    for (const key in data) {
      if (key.startsWith(row.value)) obj = { ...obj, [key]: data[key] };
    }
    return obj;
  });

  let subRowsArray = subRowsValues.map((row, index) => {
    let obj = {};
    let comparePercent;
    for (const key in row) {
      let keyValue = data[key]?.toString()?.includes('.') ? data[key]?.toFixed(2) : data[key];
      let unit = item.unit === 'none' ? '' : item.unit;
      let cell = configureCellValues(key, keyValue, unit, downloadData);
      let compareTotal = getCompareData(subRows[index], dataCompare, data);
      if (compareTotal?.totalCurrent && compareTotal?.totalCompare) {
        comparePercent = (compareTotal?.totalCurrent / compareTotal?.totalCompare) * 100;
      }
      obj = {
        ...obj,
        propertyName: 'Measured Results',
        channels: subRows[index].label,
        ...cell,
        comparePercent: comparePercent?.toFixed(2).toString()?.concat('%'),
        isSubrow: true,
      };
    }
    return obj;
  });
  return subRowsArray;
};

const configureDownloadRow = (item, data, dataCompare, downloadData) => {
  let dataArray = {};
  let obj = {};
  let comparePercent;
  if (item.value === 'household_available') {
    for (const key in data) {
      if (key.endsWith('household_available')) dataArray = { ...dataArray, [key]: data[key] };
    }
    if (data?.household_available && dataCompare?.household_available) {
      comparePercent = ((data?.household_available / dataCompare?.household_available) * 100)
        ?.toFixed(2)
        ?.toString()
        ?.concat('%');
    } else {
      comparePercent = 'N/A';
    }
    obj = { ...configureDownloadRowValues(dataArray, item, data, downloadData), comparePercent };
  } else {
    if (item?.subRows) {
      for (const key in data) {
        if (key.startsWith(item.value)) dataArray = { ...dataArray, [key]: data[key] };
      }
      let subRowsArray = configureDownloadSubRows(item, data, dataCompare, downloadData);
      let compareTotal = getCompareData(item, dataCompare, data);
      if (compareTotal?.totalCurrent && compareTotal?.totalCompare) {
        comparePercent = ((compareTotal?.totalCurrent / compareTotal?.totalCompare) * 100)
          ?.toFixed(2)
          ?.toString()
          ?.concat('%');
      } else {
        comparePercent = 'N/A';
      }
      obj = {
        ...configureDownloadRowValues(dataArray, item, data, downloadData),
        expand: true,
        subRows: subRowsArray,
        comparePercent,
      };
    } else {
      for (const key in data) {
        if (key.startsWith(item.value)) dataArray = { ...dataArray, [key]: data[key] };
      }
      let compareTotal = getCompareData(item, dataCompare, data);
      if (compareTotal?.totalCurrent && compareTotal?.totalCompare) {
        comparePercent = ((compareTotal?.totalCurrent / compareTotal?.totalCompare) * 100)
          ?.toFixed(2)
          ?.toString()
          ?.concat('%');
      } else {
        comparePercent = 'N/A';
      }
      obj = { ...configureDownloadRowValues(dataArray, item, data, downloadData), comparePercent };
    }
  }
  return obj;
};

export const configureDownloadTableData = (
  isDownload,
  options,
  storeId,
  audienceAnalysisCurrent,
  householdAvailableCurrent,
  audienceAnalysisCompare,
  householdAvailableCompare,
  downloadData
) => {
  const result = (isDownload ? options?.map((store) => store?.value) : [storeId])?.map((store) => {
    const currentStoreHousehold = householdAvailableCurrent?.find(
      (data) => data?.store_id === store
    );
    const compareStoreHousehold = householdAvailableCompare?.find(
      (data) => data?.store_id === store
    );
    const currentAudienceAnalysis = audienceAnalysisCurrent?.find(
      (data) => data?.store_id === store
    );
    const compareAudienceAnalysis = audienceAnalysisCompare?.find(
      (data) => data.store_id === store
    );

    return {
      store_id: store,
      rows: [
        ...audienceAnalysisConfig.measuredResult.map((item) =>
          item.value === 'household_available'
            ? {
                propertyName: 'Measured Results',
                channels: item.label,
                compareData: compareStoreHousehold?.household_available,
                ...configureDownloadRow(
                  item,
                  currentStoreHousehold,
                  compareStoreHousehold,
                  downloadData
                ),
              }
            : {
                propertyName: 'Measured Results',
                channels: item.label,
                ...configureDownloadRow(
                  item,
                  currentAudienceAnalysis,
                  compareAudienceAnalysis,
                  downloadData
                ),
              }
        ),
        ...audienceAnalysisConfig.calculatedPerformanceIndicators.map((item) => ({
          propertyName: 'Calculated Performance Indicators',
          channels: item.label,
          ...configureDownloadRow(
            item,
            currentAudienceAnalysis,
            compareAudienceAnalysis,
            downloadData
          ),
        })),
      ],
      columns: audienceAnalysisDownloadReportColumns?.filter(
        (col) => col?.accessor !== 'propertyName'
      ),
    };
  });
  return result;
};

const removeEmptyObjects = (result) => {
  return result[0]?.rows?.map((item) => {
    if (item?.expand) {
      return {
        ...item,
        subRows: item?.subRows?.filter((obj) => Object.keys(obj)?.length !== 0),
      };
    } else {
      return item;
    }
  });
};
